import type { Stage, Opportunity, ContactSummary, Activity, ActivityListResponse, Lead, LeadListResponse, Note, NoteListResponse } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(response.status, error.message || `Request failed: ${response.status}`);
  }

  return response.json();
}

// Stage API
export async function fetchStages(): Promise<Stage[]> {
  return fetchApi<Stage[]>('/pipeline/stages');
}

export async function fetchStage(stageId: string): Promise<Stage> {
  return fetchApi<Stage>(`/pipeline/stages/${stageId}`);
}

// Opportunity API
export async function fetchOpportunities(): Promise<Opportunity[]> {
  return fetchApi<Opportunity[]>('/opportunities');
}

export async function fetchOpportunity(oppId: string): Promise<Opportunity> {
  return fetchApi<Opportunity>(`/opportunities/${oppId}`);
}

export async function fetchOpportunitiesByStage(stageId: string): Promise<Opportunity[]> {
  return fetchApi<Opportunity[]>(`/opportunities?stage_id=${stageId}`);
}

export async function createOpportunity(opp: Partial<Opportunity>): Promise<Opportunity> {
  return fetchApi<Opportunity>('/opportunities', {
    method: 'POST',
    body: JSON.stringify(opp),
  });
}

export async function updateOpportunity(oppId: string, opp: Partial<Opportunity>): Promise<Opportunity> {
  return fetchApi<Opportunity>(`/opportunities/${oppId}`, {
    method: 'PUT',
    body: JSON.stringify(opp),
  });
}

// Stage transition API - specific endpoint for moving deals between stages
export async function transitionOpportunityStage(
  oppId: string,
  newStageId: string,
  probability?: number
): Promise<Opportunity> {
  return fetchApi<Opportunity>(`/opportunities/${oppId}/stage`, {
    method: 'PUT',
    body: JSON.stringify({ stage_id: newStageId, probability }),
  });
}

export async function deleteOpportunity(oppId: string): Promise<void> {
  await fetchApi(`/opportunities/${oppId}`, {
    method: 'DELETE',
  });
}

// Contact API (for card display)
export async function fetchContact(contactId: string): Promise<ContactSummary> {
  return fetchApi<ContactSummary>(`/contacts/${contactId}`);
}

export async function fetchContacts(contactIds: string[]): Promise<ContactSummary[]> {
  if (contactIds.length === 0) return [];
  // Batch fetch contacts
  const params = new URLSearchParams();
  contactIds.forEach(id => params.append('ids', id));
  return fetchApi<ContactSummary[]>(`/contacts?${params.toString()}`);
}

// Activity API
export interface CreateActivityRequest {
  type: Activity['type'];
  subject: string;
  description?: string;
  lead_id?: string;
  opportunity_id?: string;
  contact_id?: string;
  assigned_to?: string;
  due_date?: string;
  reminder?: string;
  metadata?: Record<string, string>;
}

export interface CompleteActivityRequest {
  outcome?: string;
  completed_at?: string;
}

export interface RescheduleActivityRequest {
  due_date: string;
  due_time?: string;
  reason?: string;
}

export async function fetchActivities(params?: {
  parent_type?: 'lead' | 'opportunity';
  parent_id?: string;
  type?: Activity['type'];
  completed?: boolean;
  limit?: number;
}): Promise<ActivityListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.parent_type) searchParams.set('parent_type', params.parent_type);
  if (params?.parent_id) searchParams.set('parent_id', params.parent_id);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.completed !== undefined) searchParams.set('completed', params.completed.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const query = searchParams.toString();
  return fetchApi<ActivityListResponse>(`/activities${query ? `?${query}` : ''}`);
}

export async function fetchActivity(activityId: string): Promise<Activity> {
  return fetchApi<Activity>(`/activities/${activityId}`);
}

export async function createActivity(activity: CreateActivityRequest): Promise<Activity> {
  return fetchApi<Activity>('/activities', {
    method: 'POST',
    body: JSON.stringify(activity),
  });
}

export async function updateActivity(activityId: string, activity: Partial<Activity>): Promise<Activity> {
  return fetchApi<Activity>(`/activities/${activityId}`, {
    method: 'PUT',
    body: JSON.stringify(activity),
  });
}

export async function deleteActivity(activityId: string): Promise<void> {
  await fetchApi(`/activities/${activityId}`, {
    method: 'DELETE',
  });
}

export async function completeActivity(activityId: string, request?: CompleteActivityRequest): Promise<Activity> {
  return fetchApi<Activity>(`/activities/${activityId}/complete`, {
    method: 'POST',
    body: JSON.stringify(request || {}),
  });
}

export async function rescheduleActivity(activityId: string, request: RescheduleActivityRequest): Promise<Activity> {
  return fetchApi<Activity>(`/activities/${activityId}/reschedule`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function fetchUpcomingActivities(limit?: number): Promise<ActivityListResponse> {
  const params = limit ? `?limit=${limit}` : '';
  return fetchApi<ActivityListResponse>(`/activities/upcoming${params}`);
}

export async function fetchOverdueActivities(limit?: number): Promise<ActivityListResponse> {
  const params = limit ? `?limit=${limit}` : '';
  return fetchApi<ActivityListResponse>(`/activities/overdue${params}`);
}

// Lead API
export interface LeadListParams {
  status?: Lead['status'];
  source?: Lead['source'];
  assignee?: string;
  limit?: number;
  offset?: number;
}

export async function fetchLeads(params?: LeadListParams): Promise<LeadListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.source) searchParams.set('source', params.source);
  if (params?.assignee) searchParams.set('assignee', params.assignee);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return fetchApi<LeadListResponse>(`/leads${query ? `?${query}` : ''}`);
}

export async function fetchLead(leadId: string): Promise<Lead> {
  return fetchApi<Lead>(`/leads/${leadId}`);
}

export interface CreateLeadRequest {
  contact_id?: string;
  source: Lead['source'];
  score?: number;
  assigned_to?: string;
  notes?: string;
  metadata?: Record<string, string>;
}

export async function createLead(lead: CreateLeadRequest): Promise<Lead> {
  return fetchApi<Lead>('/leads', {
    method: 'POST',
    body: JSON.stringify(lead),
  });
}

export interface UpdateLeadRequest {
  contact_id?: string;
  source?: Lead['source'];
  status?: Lead['status'];
  score?: number;
  assigned_to?: string;
  notes?: string;
  metadata?: Record<string, string>;
}

export async function updateLead(leadId: string, lead: UpdateLeadRequest): Promise<Lead> {
  return fetchApi<Lead>(`/leads/${leadId}`, {
    method: 'PUT',
    body: JSON.stringify(lead),
  });
}

export async function deleteLead(leadId: string): Promise<void> {
  await fetchApi(`/leads/${leadId}`, {
    method: 'DELETE',
  });
}

export async function qualifyLead(leadId: string): Promise<Lead> {
  return fetchApi<Lead>(`/leads/${leadId}/qualify`, {
    method: 'POST',
  });
}

export async function unqualifyLead(leadId: string): Promise<Lead> {
  return fetchApi<Lead>(`/leads/${leadId}/unqualify`, {
    method: 'POST',
  });
}

export interface ConvertLeadRequest {
  opportunity_name?: string;
  stage_id?: string;
  amount?: number;
  currency?: string;
  expected_close_date?: string;
  assigned_to?: string;
}

export interface ConvertLeadResponse {
  lead: Lead;
  opportunity: {
    id: string;
    name: string;
    stage_id?: string;
    amount?: number;
    currency?: string;
  };
}

export async function convertLead(leadId: string, request: ConvertLeadRequest): Promise<ConvertLeadResponse> {
  return fetchApi<ConvertLeadResponse>(`/leads/${leadId}/convert`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// Note API
export interface NoteListParams {
  parent_type: 'lead' | 'opportunity' | 'contact';
  parent_id: string;
  limit?: number;
  cursor?: string;
}

export async function fetchNotes(params: NoteListParams): Promise<NoteListResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('parent_type', params.parent_type);
  searchParams.set('parent_id', params.parent_id);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.cursor) searchParams.set('cursor', params.cursor);

  return fetchApi<NoteListResponse>(`/notes?${searchParams.toString()}`);
}

export interface CreateNoteRequest {
  parent_type: 'lead' | 'opportunity' | 'contact';
  parent_id: string;
  content: string;
  pinned?: boolean;
}

export async function createNote(note: CreateNoteRequest): Promise<Note> {
  return fetchApi<Note>('/notes', {
    method: 'POST',
    body: JSON.stringify(note),
  });
}

export async function updateNote(noteId: string, content: string): Promise<Note> {
  return fetchApi<Note>(`/notes/${noteId}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}

export async function deleteNote(noteId: string): Promise<void> {
  await fetchApi(`/notes/${noteId}`, {
    method: 'DELETE',
  });
}

export async function toggleNotePin(noteId: string): Promise<Note> {
  return fetchApi<Note>(`/notes/${noteId}/pin`, {
    method: 'POST',
  });
}

export { ApiError };

// Pipeline Stats API
export interface StageStat {
  id: string;
  name: string;
  count: number;
  value: number;
  avg_days: number;
}

export interface PipelineStats {
  stages: StageStat[];
  total_pipeline_value: number;
  total_opportunities: number;
  avg_deal_size: number;
}

export async function fetchPipelineStats(): Promise<PipelineStats> {
  return fetchApi<PipelineStats>('/pipeline/stats');
}
